import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { insertItem, patch, removeItem, updateItem } from '@ngxs/store/operators';
import { tap } from 'rxjs';
import { AdminStorageRoutes } from 'src/app/core/enums/routes.enum';
import { ApiResponse } from 'src/app/core/models/api-response';
import { OnSuccess } from 'src/app/shared/decorators/on-success.decorator';
import { ResetCurrentFilter } from 'src/app/core/state/main-table-filter.actions';
import { IdName } from 'src/app/shared/models/id-name';
import { IProductCategory } from '../models/product-category.model';
import { ProductCategoryApiService } from '../services/product-category-api.service';
import {
  CreateProductCategory,
  DeleteProductCategory,
  LoadProductCategories,
  LoadProductCategoryById,
  UpdateProductCategory,
  ClearCurrentProductCategory,
  LoadProductCategoriesList,
} from './product-category.actions';

export interface ProductCategoryStateModel {
  productCategories: IProductCategory[];
  productCategoriesList: IdName[];
  productCategoriesTotalPages: number;
  currentProductCategory: IProductCategory | undefined;
}

const PRODUCT_CATEGORY_STATE_TOKEN = new StateToken<ProductCategoryStateModel>('productCategory');

@State<ProductCategoryStateModel>({
  name: PRODUCT_CATEGORY_STATE_TOKEN,
  defaults: {
    productCategories: [],
    productCategoriesList: [],
    productCategoriesTotalPages: 0,
    currentProductCategory: undefined,
  },
})
@Injectable()
export class ProductCategoryState {
  constructor(private service: ProductCategoryApiService) {}

  @Action(LoadProductCategories)
  loadProductCategories(
    { getState, setState }: StateContext<ProductCategoryStateModel>,
    { filter }: LoadProductCategories
  ) {
    return this.service.loadProductCategories(filter).pipe(
      tap((response: ApiResponse<IProductCategory[]>) => {
        const state = getState();
        setState({
          ...state,
          productCategories: response.data,
          productCategoriesTotalPages: response.totalPages,
        });
      })
    );
  }

  @Action(LoadProductCategoriesList)
  loadProductCategoriesList({ getState, setState }: StateContext<ProductCategoryStateModel>) {
    return this.service.loadProductCategoriesList().pipe(
      tap((response: IdName[]) => {
        setState(patch({ productCategoriesList: response }));
      })
    );
  }

  @Action(LoadProductCategoryById, { cancelUncompleted: true })
  loadProductCategoryById(
    { getState, setState }: StateContext<ProductCategoryStateModel>,
    { id }: LoadProductCategoryById
  ) {
    const { productCategories } = getState();
    const exist = productCategories.some((i) => i.id === id);
    return this.service.loadProductCategoryById(id).pipe(
      tap((productCategory: IProductCategory) => {
        setState(
          patch({
            productCategories: exist
              ? updateItem<IProductCategory>((toUpdate) => toUpdate?.id === id, productCategory)
              : insertItem<IProductCategory>(productCategory),
            currentProductCategory: productCategory,
          })
        );
      })
    );
  }

  @Action(CreateProductCategory)
  @OnSuccess({ url: AdminStorageRoutes.Categories, message: 'productCategoryAdded' })
  createProductCategory(
    { setState }: StateContext<ProductCategoryStateModel>,
    { productCategory }: CreateProductCategory
  ) {
    return this.service.createProductCategory(productCategory).pipe(
      tap((response: IProductCategory) => {
        setState(
          patch({
            productCategories: insertItem<IProductCategory>(response),
          })
        );
      })
    );
  }

  @Action(UpdateProductCategory)
  @OnSuccess({ url: AdminStorageRoutes.Categories, message: 'productCategoryUpdated' })
  updateProductCategory(
    { setState }: StateContext<ProductCategoryStateModel>,
    { productCategory }: UpdateProductCategory
  ) {
    return this.service.updateProductCategory(productCategory).pipe(
      tap(() => {
        setState(
          patch({
            productCategories: updateItem<IProductCategory>(
              (toUpdate) => toUpdate?.id === productCategory.id,
              productCategory
            ),
          })
        );
      })
    );
  }

  @Action(DeleteProductCategory, { cancelUncompleted: true })
  @OnSuccess({
    url: AdminStorageRoutes.Categories,
    message: 'productCategoryDeleted',
    dispatch: ResetCurrentFilter,
  })
  deleteProductCategory(
    ctx: StateContext<ProductCategoryStateModel>,
    { id }: DeleteProductCategory
  ) {
    return this.service.deleteProductCategory(id).pipe(
      tap(() => {
        ctx.setState(
          patch({
            productCategories: removeItem<IProductCategory>(
              (toDelete: IProductCategory | undefined) => toDelete?.id === id
            ),
          })
        );
      })
    );
  }

  @Action(ClearCurrentProductCategory)
  clearCurrentProductCategory({ setState }: StateContext<ProductCategoryStateModel>) {
    setState(
      patch({
        currentProductCategory: undefined,
      })
    );
  }

  @Selector()
  static getProductCategoriesTotalPages(state: ProductCategoryStateModel) {
    return state.productCategoriesTotalPages;
  }

  @Selector()
  static productCategories(state: ProductCategoryStateModel) {
    return state.productCategories;
  }

  @Selector()
  static getProductCategoriesList(state: ProductCategoryStateModel) {
    return state.productCategoriesList;
  }

  @Selector()
  static getProductCategoryById(state: ProductCategoryStateModel) {
    return (id: string) => {
      return state.productCategories.find(
        (productCategory: IProductCategory) => productCategory.id === id
      );
    };
  }

  @Selector()
  static getCurrentProductCategory(state: ProductCategoryStateModel) {
    return state.currentProductCategory;
  }
}
